import * as React from 'react';
import {type Matcher, type PropsBase, type PropsSingle} from 'react-day-picker';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {toCssSize} from '../../lib/utils';
import {isMobileModalModeAvailable} from '../../lib/runtimeSettings';
import {useIsMobile, useOutsideClick} from '../../hooks';
import {FieldErrorMessage} from '../../field-error-message';
import {Fieldset} from '../_fieldset';
import {isDayBlocked} from '../date-range-picker/isDayBlocked';
import {
  DEFAULT_DISPLAY_FORMAT,
  formatDate,
  parseDate,
  toDate,
} from '../date-range-picker/dateUtils';
import type {DateInput} from '../date-range-picker/dateUtils';
import {DateRangePopover} from '../date-range-picker/components/DateRangePopover';
import {useSingleDateValue} from './hooks/useSingleDateValue';
import {useSingleDateText} from './hooks/useSingleDateText';
import {SingleDateInput} from './components/SingleDateInput';
import {SingleDateCalendar} from './components/SingleDateCalendar';

type OpenDirection = 'up' | 'down';

type SingleDatePickerProps = Omit<Partial<PropsSingle>, 'mode' | 'selected'> &
  Omit<PropsBase, 'mode' | 'selected' | 'disabled'> & {
    label?: string;
    value?: DateInput | null;
    defaultValue?: DateInput | null;
    onChange?: (value: Date | undefined, name?: string) => void;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    onBlur?: (value: Date | undefined) => void;
    name?: string;
    invalid?: boolean;
    error?: string;
    disabled?: boolean;
    readOnly?: boolean;
    loading?: boolean;
    optional?: boolean | string;
    tooltip?: React.ReactNode;
    className?: string;
    displayFormat?: string;
    placeholder?: string;
    minDate?: DateInput;
    maxDate?: DateInput;
    disabledDays?: Matcher | Matcher[];
    hideClearDate?: boolean;
    hideCalendarIcon?: boolean;
    hideErrorMessage?: boolean;
    helperText?: string;
    width?: number | string;
    numberOfMonths?: number;
    openDirection?: OpenDirection;
    autoFocusOnOpen?: boolean;
  };

export type SingleDatePickerImperativeProps = {
  setDate: (date?: DateInput) => void;
  focus: () => void;
  open: () => void;
  close: () => void;
};

const SingleDatePicker = React.forwardRef<
  SingleDatePickerImperativeProps,
  SingleDatePickerProps
>(function SingleDatePicker(
  {
    label,
    value: externalValue,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    name,
    invalid,
    error,
    disabled,
    readOnly,
    loading,
    optional,
    tooltip,
    className,
    displayFormat = DEFAULT_DISPLAY_FORMAT,
    placeholder,
    minDate,
    maxDate,
    disabledDays,
    hideClearDate,
    hideCalendarIcon,
    hideErrorMessage,
    helperText,
    width,
    numberOfMonths = 1,
    openDirection = 'down',
    autoFocusOnOpen = true,
    components: customComponents,
    ...dayPickerProps
  },
  ref,
) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const reactId = React.useId();
  const baseId = name ?? `dash-singledate-${reactId}`;
  const inputId = baseId;
  const labelId = `${baseId}-label`;
  const errorId = `${baseId}-error`;

  const normalizedValue = React.useMemo<Date | undefined>(
    () => (externalValue === undefined ? undefined : toDate(externalValue)),
    [externalValue],
  );
  const normalizedDefaultValue = React.useMemo<Date | undefined>(
    () => (defaultValue === undefined ? undefined : toDate(defaultValue)),
    [defaultValue],
  );

  const {value, commit} = useSingleDateValue({
    value: normalizedValue,
    defaultValue: normalizedDefaultValue,
    onChange,
    name,
  });

  const normalizedMinDate = React.useMemo(() => toDate(minDate), [minDate]);
  const normalizedMaxDate = React.useMemo(() => toDate(maxDate), [maxDate]);

  const formatter = React.useMemo(() => formatDate(displayFormat), [displayFormat]);
  const parser = React.useMemo(() => parseDate(displayFormat), [displayFormat]);

  const [month, setMonth] = React.useState<Date>(value ?? new Date());
  const isPreloadedRef = React.useRef(false);
  React.useEffect(() => {
    if (value && !isPreloadedRef.current) {
      setMonth(value);
      isPreloadedRef.current = true;
    }
  }, [value]);

  const closeCalendarRef = React.useRef<() => void>(() => {});

  const handleComplete = React.useCallback(() => {
    inputRef.current?.blur();
    closeCalendarRef.current();
  }, []);

  const {text, setText, handleChange, handleBlur} = useSingleDateText({
    value,
    format: formatter,
    parse: parser,
    displayFormat,
    onCommit: commit,
    onBlur,
    onComplete: handleComplete,
  });

  const [isOpen, setIsOpen] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);
  const [autoFocus, setAutoFocus] = React.useState(false);
  const isMobile = useIsMobile() && isMobileModalModeAvailable();
  const {t} = useTranslation();

  const drawerTitle = label ?? t('select_date');
  const drawerDescription = label ?? t('pick_date');
  const isEmpty = !value;
  const isBlocked = Boolean(disabled) || Boolean(loading);
  const isInvalid = Boolean(invalid || error);
  const isActive = isFocused || isOpen;
  const wrapperWidth = toCssSize(width);

  const closeCalendar = React.useCallback(() => {
    setIsOpen(false);
    setIsFocused(false);
    setAutoFocus(false);
    if (value) setMonth(value);
  }, [value]);
  closeCalendarRef.current = closeCalendar;

  const openCalendar = React.useCallback(() => {
    if (isBlocked || readOnly) return;
    setIsOpen(true);
  }, [isBlocked, readOnly]);

  useOutsideClick({
    elementRef: containerRef,
    onOutsideClick: closeCalendar,
    isDisabled: !isOpen || isMobile,
  });

  const handlePickerSelect: NonNullable<PropsSingle['onSelect']> = React.useCallback(
    selected => {
      commit(selected);
      if (selected) setMonth(selected);
      closeCalendar();
    },
    [closeCalendar, commit],
  );

  const handleReset = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    commit(undefined);
    setText('');
    setMonth(new Date());
  };

  const disabledMatchers = React.useMemo<Matcher[]>(() => {
    const result: Matcher[] = [
      isDayBlocked({minDate: normalizedMinDate, maxDate: normalizedMaxDate}),
    ];
    if (Array.isArray(disabledDays)) result.push(...disabledDays);
    else if (disabledDays) result.push(disabledDays);
    return result;
  }, [disabledDays, normalizedMaxDate, normalizedMinDate]);

  const toggleCalendar = () => {
    if (isOpen) {
      closeCalendar();
      return;
    }
    openCalendar();
    if (autoFocusOnOpen) setAutoFocus(true);
  };

  React.useImperativeHandle(
    ref,
    () => ({
      setDate: date => {
        const next = toDate(date);
        if (next) setMonth(next);
        commit(next);
        setText(next ? formatter(next) : '');
      },
      focus: () => inputRef.current?.focus(),
      open: openCalendar,
      close: closeCalendar,
    }),
    [closeCalendar, commit, formatter, openCalendar, setText],
  );

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-[var(--field-width,100%)] max-w-[var(--field-max-width,296px)]',
        disabled && 'cursor-not-allowed opacity-50',
        loading && 'cursor-progress opacity-50',
        className,
      )}
      style={wrapperWidth ? {width: wrapperWidth} : undefined}
    >
      <div className="relative min-h-[var(--field-min-height,48px)] w-full">
        <div className="relative w-full">
          <SingleDateInput
            id={inputId}
            name={name}
            labelId={labelId}
            text={text}
            placeholder={placeholder}
            inputRef={inputRef}
            isEmpty={isEmpty}
            isFocused={isActive}
            isBlocked={isBlocked}
            isInvalid={isInvalid}
            loading={loading}
            readOnly={readOnly}
            hideClearDate={hideClearDate}
            hideCalendarIcon={hideCalendarIcon}
            isOpen={isOpen}
            clearLabel={t('clear_date')}
            openCalendarLabel={t('open_calendar')}
            onTextChange={handleChange}
            onFocus={event => {
              onFocus?.(event);
              setIsFocused(true);
              if (!readOnly && !isBlocked) setIsOpen(true);
            }}
            onBlur={() => {
              setIsFocused(false);
              handleBlur();
            }}
            onRowClick={() => {
              if (isBlocked || readOnly) return;
              if (!isFocused) inputRef.current?.focus();
            }}
            onReset={handleReset}
            onToggleCalendar={toggleCalendar}
          />
          <Fieldset
            isFocused={isActive}
            invalid={isInvalid}
            isEmpty={isEmpty}
            isActivated={!isEmpty || isActive}
            disabled={disabled}
            loading={loading}
            readOnly={readOnly}
            htmlFor={inputId}
            labelId={labelId}
            legend={label}
            label={label}
            tooltip={tooltip}
          />
          <DateRangePopover
            isOpen={isOpen && !isMobile}
            isMobile={false}
            openDirection={openDirection}
            drawerTitle={drawerTitle}
            drawerDescription={drawerDescription}
            onClose={closeCalendar}
          >
            <SingleDateCalendar
              value={value}
              month={month}
              onMonthChange={setMonth}
              onSelect={handlePickerSelect}
              numberOfMonths={numberOfMonths}
              minDate={normalizedMinDate}
              maxDate={normalizedMaxDate}
              autoFocus={autoFocus}
              disabledMatchers={disabledMatchers}
              components={customComponents}
              {...dayPickerProps}
            />
          </DateRangePopover>
        </div>
        <DateRangePopover
          isOpen={isOpen && isMobile}
          isMobile={true}
          openDirection={openDirection}
          drawerTitle={drawerTitle}
          drawerDescription={drawerDescription}
          onClose={closeCalendar}
        >
          <SingleDateCalendar
            value={value}
            month={month}
            onMonthChange={setMonth}
            onSelect={handlePickerSelect}
            numberOfMonths={numberOfMonths}
            minDate={normalizedMinDate}
            maxDate={normalizedMaxDate}
            autoFocus={autoFocus}
            disabledMatchers={disabledMatchers}
            components={customComponents}
            {...dayPickerProps}
          />
        </DateRangePopover>
        {!error && optional && (
          <span className="mt-[1px] block text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
            {typeof optional === 'string' ? optional : t('optional')}
          </span>
        )}
        {!error && helperText && (
          <span className="mt-[1px] block text-[12px] font-normal text-[var(--chekin-color-gray-1)]">
            {helperText}
          </span>
        )}
        {error && !hideErrorMessage && (
          <FieldErrorMessage
            id={errorId}
            message={error}
            className="mt-[1px] text-[14px]"
          />
        )}
      </div>
    </div>
  );
});

export {SingleDatePicker};
export type {SingleDatePickerProps, OpenDirection};
